// 1. Framework / library imports
import { forwardRef, memo, useCallback, useEffect, useImperativeHandle, useRef, useState } from "react";

// 2. Helpers & Hooks
import { useTranslate } from "helpers";

// 3. API & Types
import { deleteFleetVehicleTireAPI, getFleetVehicleTiresAPI } from "services/api/fleet/vehicles/tires/api";
import {
    FleetVehicleTireRow,
} from "services/api/fleet/vehicles/tires/types";
import { DataWithPagination } from "services/api";
import { initialDataWithPagination } from "services/api/consts";
import { getVehicleTypeAPI } from "services/api/catalog/vehicles/types/api";
import { VehicleTypeAxle } from "services/api/catalog/vehicles/types/types";
import { createFleetTireAPI, createFleetTireSetAPI, getTireTypesAPI } from "services/api/fleet/tires/api";
import { FleetTireAddRow } from "services/api/fleet/tires/types";
import { getTireManufacturersAPI } from "services/api/calculations/tire/api";
import { PaginationDirection } from "services/url";

// 4. UI Components
import { Button } from "ui/components/Button/Button";
import { Dialog } from "ui/components/Dialog";
import { Loader } from "ui/components/Loader";

// 5. Local Components
import TireAddModal from "ui/modules/fleet/tires/pages/TireAddModal/TireAddModal";
import TireInlineTable, { TireInlineTableRef } from "./components/TireInlineTable";

// 6. Local Types
import { VehicleTireFormProps, VehicleTireFormRef } from "./types";


const VehicleTireForm = forwardRef<VehicleTireFormRef, VehicleTireFormProps>((props, ref) => {
    const { formValues, setFormValues, setVehicleInfo, errors } = props;

    const tireTableRef = useRef<TireInlineTableRef>(null);

    const { t } = useTranslate();

    const [searchResults, setSearchResults] = useState<
        DataWithPagination<FleetVehicleTireRow>
    >(initialDataWithPagination);

    const [tireId, setTireId] = useState<string>("");
    const [openDialog, setOpenDialog] = useState(false);
    const [showAddNewModal, setShowAddNewModal] = useState<boolean>(false);

    const [catalogAxles, setCatalogAxles] = useState<VehicleTypeAxle[]>([]);
    const [isInitializing, setIsInitializing] = useState<boolean>(true);
    const catalogFetchedRef = useRef(false);
    const defaultTiresCreatedRef = useRef(false);
    const initializingRef = useRef(false);

    // Inicijalizacija: dohvati katalog guma, pa postojeće gume, pa po potrebi kreiraj default
    useEffect(() => {
        if (!formValues.id) return;
        if (initializingRef.current) return; // Sprječava dvostruko pokretanje
        initializeTires();
    }, [formValues.id]);

    /** Ažurira summary karticu s podacima o gumama (iz API rezultata) */
    const updateTireSummary = (tires: FleetVehicleTireRow[]) => {
        if (tires.length === 0) return;
        const first = tires[0];
        setVehicleInfo(prev => ({
            ...prev,
            tireDimensions: first.width && first.height && first.radius
                ? `${first.width}/${first.height}/R${first.radius}`
                : null,
            tireManufacturer: first.tireManufacturer?.name ?? null,
            tireType: first.tireType?.name ?? null,
            dot: first.dot ?? null,
            profileDepth: first.profileDepth ?? null,
            rof: first.rof ?? null,
            tireCount: tires.length,
        }));
    };

    /** Ažurira summary karticu kad korisnik promijeni vrijednosti u inline tablici */
    const handleEditChange = useCallback((summary: {
        tireDimensions: string | null;
        tireManufacturer: string | null;
        tireType: string | null;
        dot: number | null;
        profileDepth: number | null;
        rof: boolean | null;
    }) => {
        setVehicleInfo(prev => ({
            ...prev,
            ...summary,
        }));
    }, [setVehicleInfo]);

    /** Pokreće cijeli proces inicijalizacije guma u jednom toku bez trzanja */
    const initializeTires = async () => {
        if (!formValues.id) {
            setIsInitializing(false);
            return;
        }

        initializingRef.current = true;
        setIsInitializing(true);

        // 1. Dohvati katalog osovina iz tipa vozila (ako još nije dohvaćen)
        let axles = catalogAxles;
        if (!catalogFetchedRef.current && formValues.vehicleTypeId && formValues.vehicleManufacturerId && formValues.vehicleModelId) {
            catalogFetchedRef.current = true;
            try {
                const vehicleType = await getVehicleTypeAPI(
                    formValues.vehicleManufacturerId,
                    formValues.vehicleModelId,
                    formValues.vehicleTypeId,
                );
                if (vehicleType.axles && vehicleType.axles.length > 0) {
                    axles = vehicleType.axles;
                    setCatalogAxles(axles);
                }
            } catch { /* nastavi bez kataloga */ }
        }

        // 2. Dohvati postojeće gume iz baze
        let existingCount = 0;
        try {
            const results = await getFleetVehicleTiresAPI(formValues.id, searchResults.pagination);
            setSearchResults(results);
            existingCount = results.pagination.totalCount ?? 0;
            setFormValues(prev => ({ ...prev, tiresCount: existingCount }));
            updateTireSummary(results.data);
        } catch {
            setSearchResults(initialDataWithPagination);
            setFormValues(prev => ({ ...prev, tiresCount: 0 }));
        }

        // 3. Ako nema guma i imamo katalog osovina — kreiraj preddefinirane gume
        if (existingCount === 0 && axles.length > 0 && !defaultTiresCreatedRef.current) {
            defaultTiresCreatedRef.current = true;
            await createDefaultTiresFromCatalog(axles);

            // 4. Osvježi listu nakon kreiranja
            try {
                const results = await getFleetVehicleTiresAPI(formValues.id, searchResults.pagination);
                setSearchResults(results);
                setFormValues(prev => ({ ...prev, tiresCount: results.pagination.totalCount ?? 0 }));
                updateTireSummary(results.data);
            } catch { /* already handled */ }
        }

        setIsInitializing(false);
        initializingRef.current = false;
    };

    /** Određuje ID tipa gume (ljetna/zimska) na temelju trenutnog mjeseca.
     *  Travanj–listopad = ljetne, studeni–ožujak = zimske.
     *  Ako ne pronađe odgovarajući tip, vraća prvi dostupni kao fallback. */
    const getSeasonalTireTypeId = async (): Promise<string | null> => {
        try {
            const result = await getTireTypesAPI({
                pageNumber: 1,
                pageSize: 100,
                sort: { sortBy: "name", direction: PaginationDirection.Ascending },
            });
            const types = result.data;

            const month = new Date().getMonth() + 1;
            const isSummer = month >= 4 && month <= 10;
            const keyword = isSummer ? "ljetn" : "zimsk";

            const match = types.find(t =>
                t.name?.toLowerCase().includes(keyword)
            );

            return match?.id ?? types[0]?.id ?? null;
        } catch {
            return null;
        }
    };

    /** Dohvaća ID prvog dostupnog proizvođača guma kao predodabranu vrijednost */
    const getDefaultTireManufacturerId = async (): Promise<string | null> => {
        try {
            const result = await getTireManufacturersAPI({
                pageNumber: 1,
                pageSize: 1,
                sort: { sortBy: "name", direction: PaginationDirection.Ascending },
            });
            return result.data[0]?.id ?? null;
        } catch {
            return null;
        }
    };

    /** Kreira preddefinirane gume u bazi na temelju kataloga osovina tipa vozila
     *  i automatski ih stavlja u novi TireSet. */
    const createDefaultTiresFromCatalog = async (axles: VehicleTypeAxle[]) => {
        if (!formValues.id || axles.length === 0) return;

        try {
            const [tireTypeId, tireManufacturerId] = await Promise.all([
                getSeasonalTireTypeId(),
                getDefaultTireManufacturerId(),
            ]);

            const allCreatedTires: FleetTireAddRow[] = [];

            for (const axle of axles) {
                const created = await createFleetTireAPI({
                    width: axle.width,
                    height: axle.ratio,
                    radius: axle.diameter,
                    quantity: 2,
                    tireTypeId,
                    tireManufacturerId,
                    dot: 0,
                    profileDepth: 0,
                    rof: false,
                    vehicleId: formValues.id,
                    axlePosition: axle.axlePosition,
                }, t);

                if (created) allCreatedTires.push(...created);
            }

            if (allCreatedTires.length > 0) {
                await createFleetTireSetAPI({
                    id: null,
                    code: null,
                    isActive: true,
                    tires: allCreatedTires,
                    vehicle: { id: formValues.id } as any,
                    warehouse: null,
                    tireSetReplacement: null,
                }, t);
            }
        } catch {
            // greška pri kreiranju predefiniranih guma
        }
    };

    /** Osvježava listu guma iz baze nakon dodavanja ili brisanja */
    const refreshTires = useCallback(() => {
        if (formValues.id) {
            getFleetVehicleTiresAPI(formValues.id, searchResults.pagination)
                .then(results => {
                    setSearchResults(results);
                    setFormValues(prev => ({ ...prev, tiresCount: results.pagination.totalCount ?? 0 }));
                })
                .catch(() => {
                    setSearchResults(initialDataWithPagination);
                    setFormValues(prev => ({ ...prev, tiresCount: 0 }));
                });
        }
    }, [formValues.id, searchResults.pagination]);

    /** Otvara dijalog za potvrdu brisanja gume */
    const handleDeleteClick = useCallback(
        (event: React.MouseEvent<HTMLDivElement>, id: string) => {
            event.stopPropagation();
            event.preventDefault();
            setTireId(id);
            setOpenDialog(true);
        },
        []
    );

    /** Otvara modal za ručno dodavanje gume */
    const handleAddClick = useCallback(() => {
        setShowAddNewModal(true);
    }, []);

    /** Osvježava tablicu nakon uspješnog dodavanja nove gume */
    const handleAddSuccess = useCallback(() => {
        refreshTires();
        setShowAddNewModal(false);
    }, [refreshTires]);

    /** Izvršava brisanje gume putem API-ja i osvježava tablicu */
    const handleTireDeleteDialogConfirm = useCallback(async () => {
        if (!tireId) return;

        try {
            const deleted = await deleteFleetVehicleTireAPI(tireId, t);
            if (deleted) refreshTires();
            setOpenDialog(false);
            setTireId("");
        } catch (err) {
            console.error("Greška prilikom brisanja gume:", err);
        }
    }, [tireId, t, refreshTires]);

    /** Zatvara dijalog za brisanje bez izvršavanja akcije */
    const handleTireDeleteDialogClose = useCallback(() => {
        setTireId("");
        setOpenDialog(false);
    }, []);

    /** Proslijedi saveAll metodu prema wizardu */
    useImperativeHandle(ref, () => ({
        saveAll: async () => {
            if (tireTableRef.current) {
                return tireTableRef.current.saveAll();
            }
            return 0;
        }
    }), []);

    if (!formValues.id || isInitializing) {
        return (
            <div className="relative flex items-center justify-center px-4 h-[calc(100vh-150px)]">
                <Loader isLoading={true}> </Loader>
            </div>
        );
    }

    // Bez guma se korak smije preskočiti — tip vozila nema uvijek osovine u katalogu,
    // pa se preddefinirane gume ne mogu složiti. Dodaju se ovdje ili naknadno na vozilu.
    const hasTires = searchResults.data.length > 0;

    return (
        <>
            <div className="relative px-4 pt-12 pb-6">
                <h1 className="text-2xl font-bold text-slate-900 text-center mb-1">{t("admin.fleet.vehicle.edit.tire.title")}</h1>
                <p className="text-center text-sm text-slate-500 mb-4 max-w-2xl mx-auto mb-1">
                    Podatke o gumama uređujete u prvom redu, promjene se primjenjuju na sve gume odjednom.
                </p> 
                <img src="/media/images/vehicles/tires.webp" alt="" className="mx-auto h-[200px] my-4" />
                <div className="mt-6 overflow-x-auto">
                    {errors?.tiresCount && (
                        <p className="text-center text-sm text-red-600 mb-4">{errors.tiresCount}</p>
                    )}
                    {hasTires ? (
                        <TireInlineTable
                            ref={tireTableRef}
                            tires={searchResults.data}
                            onDeleteClick={handleDeleteClick}
                            onEditChange={handleEditChange}
                        />
                    ) : (
                        <div className="flex flex-col items-center gap-3 py-6">
                            <p className="text-sm text-slate-500">
                                Gume nisu unesene. Možete ih dodati sada ili preskočiti korak i
                                dodati ih kasnije na vozilu.
                            </p>
                            <Button
                                type="button"
                                variant="outlined"
                                onClick={handleAddClick}
                            >
                                Dodaj gume
                            </Button>
                        </div>
                    )}
                </div>
            </div>
            <TireAddModal
                show={showAddNewModal}
                vehicleId={formValues.id}
                defaultAxles={catalogAxles}
                onSuccess={handleAddSuccess}
                onClose={() => setShowAddNewModal(false)}
            />
            <Dialog
                open={openDialog}
                title={t("admin.fleet.vehicle.edit.tire.dialog.delete.title")}
                message={t("admin.fleet.vehicle.edit.tire.dialog.delete.message")}
                confirmText={t("admin.fleet.vehicle.edit.tire.dialog.delete.confirmtext")}
                cancelText={t("admin.fleet.vehicle.edit.tire.dialog.delete.canceltext")}
                onConfirm={handleTireDeleteDialogConfirm}
                onCancel={handleTireDeleteDialogClose}
            />
        </>
    );
});

VehicleTireForm.displayName = "VehicleTireForm";
export default memo(VehicleTireForm);
